<template>
  <TirPmPopover
    ref="$popover"
    :position="PopoverPositionEnum.Top"
    @open="onOpen"
    @close="emit('update:isOpened', false)"
  >
    <slot />
    <template #content>
      <div class="file-preview-edit-name-popover">
        <TirPmInput
          v-model="inputValue"
          label="Название файла"
          :is-with-hint="false"
          auto-focus
          @enter="save"
        />
        <div class="file-preview-edit-name-popover__buttons" @click.stop>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.PrimaryState"
            :size="TirPmButtonSizeEnum.Small"
            :is-disabled="isDisabledSave"
            :is-loading="isLoading"
            @click="save"
          >
            Сохранить
          </TirPmButton>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :size="TirPmButtonSizeEnum.Small"
            @click="close"
          >
            Отмена
          </TirPmButton>
        </div>
      </div>
    </template>
  </TirPmPopover>
</template>

<script lang="ts" setup>
import { TirPmPopover, PopoverPositionEnum } from "tir-pm-popover";
import { TirPmInput } from "tir-pm-input";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from "tir-pm-button";
import { ref, onMounted, watch, computed } from "vue";

const emit = defineEmits<{
  /**Событие сохранения названия файла */
  (e: "saveFileName", fileName: string): void;
  /**Событие обновления флага обозначабщего открыт ли поповер */
  (e: "update:isOpened", value: boolean): void;
}>();

const props = defineProps({
  /**Поповер открыт */
  isOpened: {
    type: Boolean,
    default: false,
  },
  /**Имя файла */
  fileName: {
    type: String,
    default: "",
  },
  /**Загрузка */
  isLoading: {
    type: Boolean,
    default: false,
  },
});

/**Ссылка на компонент поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();

const inputValue = ref("");

const isDisabledSave = computed(() => !inputValue.value?.trim());

const removeFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return fileName; // Если точки нет, возвращаем исходное имя
  return fileName.substring(0, lastDotIndex);
};

const save = () => {
  if (isDisabledSave.value) return;
  emit("saveFileName", inputValue.value);
};

const onOpen = () => {
  inputValue.value = removeFileExtension(props.fileName);
  emit("update:isOpened", true);
};

const close = () => $popover.value?.close();

const open = () => $popover.value?.open();

watch(
  () => props.fileName,
  () => {
    inputValue.value = removeFileExtension(props.fileName);
  }
);

watch(
  () => props.isLoading,
  () => {
    if (!props.isLoading) $popover.value?.close();
  }
);

onMounted(() => {
  inputValue.value = removeFileExtension(props.fileName);
});

defineExpose({
  close,
  open,
});
</script>

<style lang="scss" scoped>
@import "./file-preview-edit-name-popover.scss";
</style>
